import React, { useState, useEffect, useMemo } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import BlockIcon from "@mui/icons-material/Block";
import RemoveShoppingCartIcon from "@mui/icons-material/RemoveShoppingCart";
import AdminCrossBoardService from "./AdminCrossBoardService";
import Logger from "../../../services/Logger";
import { getFillTabIndex, setFillTabIndex, removeFillTabEntry, FILL_TAB_KEY_CROSS } from "../../pzl_common/util/fillTabStorage";
import { formatMeaning } from "../../pzl_common/FillDialogMeaning";
import { fillDialogCloseIconButtonSx, fillDialogTitleSx } from "../../pzl_common/fillDialogSafeArea";

function getPositionsSet(item) {
    const pos = item?.coordinates?.positions ?? item?.positions ?? [];
    return new Set((Array.isArray(pos) ? pos : []).map((p) => `${p.row}-${p.col}`));
}

function positionsOverlapWithCart(item, cartItems) {
    if (!item || !Array.isArray(cartItems) || cartItems.length === 0) return false;
    const setA = getPositionsSet(item);
    if (setA.size === 0) return false;
    for (const c of cartItems) {
        const setB = getPositionsSet(c);
        for (const key of setA) if (setB.has(key)) return true;
    }
    return false;
}

/** Set of "row-col" for cells that already have a letter in the puzzle. */
function buildFilledCellKeys(filledCoordinates) {
    const set = new Set();
    if (!Array.isArray(filledCoordinates)) return set;
    filledCoordinates.forEach((entry) => {
        const posList = entry?.positions ?? entry?.coordinates?.positions ?? [];
        (Array.isArray(posList) ? posList : []).forEach((p) => set.add(`${p.row}-${p.col}`));
    });
    return set;
}

function countCellsWillFill(positions, filledCellKeys) {
    if (!Array.isArray(positions) || !filledCellKeys || filledCellKeys.size === 0) return positions?.length ?? 0;
    return positions.filter((p) => !filledCellKeys.has(`${p.row}-${p.col}`)).length;
}

/**
 * Cross Fill dialog: one popup with 3 tabs.
 * 1 = Fill Assoc (getFillAssocBedrock), 2 = Candidate Words (getCandidatesPatternTags), 3 = Final-6 (getFinal6Candidates).
 */
export default function CrossFillDialog({
    open,
    onClose,
    crossTplId,
    puzzleTitle,
    puzzleType,
    tags = [],
    language = "english",
    thresholdMet = false,
    thresholdPercentage,
    cellsNeeded,
    emptyCellCount,
    onReadyToReview,
    readyToReviewDisabled = false,
    readyToReviewLoading = false,
    onTab1PlaceWord,
    onTab2Add,
    onTab3Add,
    onPlaceWords,
    adding = false,
    filledCoordinates = [],
    onAddBannedWord
}) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const filledCellKeys = useMemo(() => buildFilledCellKeys(filledCoordinates), [filledCoordinates]);
    const [activeTab, setActiveTab] = useState(1);
    const [cart, setCart] = useState([]);

    // Restore tab from storage when dialog opens; remove entry when puzzle has no empty cells
    useEffect(() => {
        if (!open || !crossTplId) return;
        if (thresholdMet || emptyCellCount === 0) {
            removeFillTabEntry(FILL_TAB_KEY_CROSS, crossTplId);
            return;
        }
        const stored = getFillTabIndex(FILL_TAB_KEY_CROSS, crossTplId);
        if (typeof stored === "number" && stored >= 1 && stored <= 3) {
            setActiveTab(stored);
        }
    }, [open, crossTplId, thresholdMet, emptyCellCount]);

    // Tab 1: Fill Assoc (GET result, wordsWithPositions, place word)
    const [tab1Result, setTab1Result] = useState(null);
    const [tab1Error, setTab1Error] = useState(null);
    const [tab1Loading, setTab1Loading] = useState(false);
    const [tab1Index, setTab1Index] = useState(0);

    // Tab 2: Candidate Words
    const [tab2Items, setTab2Items] = useState([]);
    const [tab2Loading, setTab2Loading] = useState(false);
    const [tab2Index, setTab2Index] = useState(0);

    // Tab 3: Final-6 (getFinal6Candidates)
    const [tab3Result, setTab3Result] = useState(null);
    const [tab3Error, setTab3Error] = useState(null);
    const [tab3Loading, setTab3Loading] = useState(false);
    const [tab3Index, setTab3Index] = useState(0);

    const addToCart = (item) => {
        if (!item) return;
        const positions = item?.coordinates?.positions ?? item?.positions ?? [];
        if (positions.length === 0) return;
        if (countCellsWillFill(positions, filledCellKeys) === 0) return;
        if (positionsOverlapWithCart(item, cart)) return;
        if (cart.some((c) => (c.word || "").trim() === (item.word || "").trim() && getPositionsSet(c).size > 0 && getPositionsSet(item).size > 0)) return;
        setCart((prev) => [...prev, item]);
    };
    const isItemInCart = (item) =>
        !!item && cart.some((c) => (c.word || "").trim() === (item.word || "").trim() && getPositionsSet(c).size > 0 && getPositionsSet(item).size > 0);
    const filterByCart = (list) => (Array.isArray(list) ? list.filter((item) => !positionsOverlapWithCart(item, cart)) : []);

    const tab1Words = tab1Result?.wordsWithPositions ?? [];
    const tab1Filtered = filterByCart(tab1Words);
    const tab1Total = tab1Filtered.length;
    const tab1Item = tab1Filtered[tab1Index];

    useEffect(() => {
        if (!open) {
            setTab1Result(null);
            setTab1Error(null);
            setTab1Index(0);
            setTab2Items([]);
            setTab2Index(0);
            setTab3Result(null);
            setTab3Error(null);
            setTab3Index(0);
            setCart([]);
        }
    }, [open]);

    useEffect(() => {
        setTab1Index((i) => Math.min(i, Math.max(0, tab1Total - 1)));
    }, [tab1Total]);

    const tab2Filtered = filterByCart(tab2Items);
    const tab2Total = tab2Filtered.length;
    const tab2Item = tab2Filtered[tab2Index];

    useEffect(() => {
        setTab2Index((i) => Math.min(i, Math.max(0, tab2Filtered.length - 1)));
    }, [tab2Filtered.length]);

    const tab3Words = tab3Result?.wordsWithPositions ?? [];
    const tab3Filtered = filterByCart(tab3Words);
    const tab3Total = tab3Filtered.length;
    const tab3Item = tab3Filtered[tab3Index];

    useEffect(() => {
        setTab3Index((i) => Math.min(i, Math.max(0, tab3Total - 1)));
    }, [tab3Total]);

    const handleTabChange = (e, value) => {
        if (value != null) {
            setActiveTab(value);
            if (crossTplId) setFillTabIndex(FILL_TAB_KEY_CROSS, crossTplId, value);
        }
    };

    // Tab 1: Fill Assoc (GET candidates, show result, place word)
    const handleTab1Refresh = async () => {
        if (!crossTplId) return;
        setCart([]);
        setTab1Loading(true);
        setTab1Error(null);
        setTab1Result(null);
        try {
            const result = await AdminCrossBoardService.getFillAssocBedrock(crossTplId);
            setTab1Result(result);
        } catch (err) {
            Logger.error("Error fetching fill assoc:", err);
            setTab1Error(err?.message || "Fill assoc failed.");
            setTab1Result(null);
        } finally {
            setTab1Loading(false);
        }
    };

    const handleTab1PlaceWord = async () => {
        if (!tab1Item || !onTab1PlaceWord) return;
        const normalized = {
            ...tab1Item,
            positions: tab1Item.coordinates?.positions ?? tab1Item.positions
        };
        await onTab1PlaceWord(normalized);
        setTab1Result((prev) =>
            prev?.wordsWithPositions
                ? { ...prev, wordsWithPositions: prev.wordsWithPositions.filter((w) => w !== tab1Item) }
                : prev
        );
    };

    const handleTab1Place = async () => {
        const cartCount = cart.length;
        if (cartCount > 0) {
            if (typeof onPlaceWords === "function") {
                await onPlaceWords(cart);
                const cartKeys = new Set(cart.map((c) => `${(c.word || "").trim()}|${[...getPositionsSet(c)].sort().join(",")}`));
                setTab1Result((prev) => {
                    if (!prev?.wordsWithPositions) return prev;
                    return { ...prev, wordsWithPositions: prev.wordsWithPositions.filter((w) => !cartKeys.has(`${(w.word || "").trim()}|${[...getPositionsSet(w)].sort().join(",")}`)) };
                });
                setCart([]);
            } else if (onTab1PlaceWord) {
                const cartKeys = new Set(cart.map((c) => `${(c.word || "").trim()}|${[...getPositionsSet(c)].sort().join(",")}`));
                for (const item of cart) {
                    const normalized = { ...item, positions: item?.coordinates?.positions ?? item?.positions };
                    await onTab1PlaceWord(normalized);
                }
                setCart([]);
                setTab1Result((prev) => {
                    if (!prev?.wordsWithPositions) return prev;
                    return { ...prev, wordsWithPositions: prev.wordsWithPositions.filter((w) => !cartKeys.has(`${(w.word || "").trim()}|${[...getPositionsSet(w)].sort().join(",")}`)) };
                });
            }
        } else if (tab1Item && onTab1PlaceWord) {
            await handleTab1PlaceWord();
        }
    };

    // Tab 2: Candidate Words
    const handleTab2Refresh = async () => {
        if (!crossTplId) return;
        setCart([]);
        setTab2Loading(true);
        try {
            const result = await AdminCrossBoardService.getCandidatesPatternTags(crossTplId);
            const items = Array.isArray(result) ? result : [];
            const sorted = [...items].sort((a, b) => (b.info?.score ?? 0) - (a.info?.score ?? 0));
            setTab2Items(sorted);
        } catch (err) {
            Logger.error("Error fetching candidate words:", err);
        } finally {
            setTab2Loading(false);
        }
    };

    // Tab 3: Final-6 (getFinal6Candidates)
    const handleTab3Refresh = async () => {
        if (!crossTplId) return;
        setCart([]);
        setTab3Loading(true);
        setTab3Error(null);
        setTab3Result(null);
        try {
            const result = await AdminCrossBoardService.getFinal6Candidates(crossTplId);
            setTab3Result(result);
        } catch (err) {
            Logger.error("Error fetching final-6:", err);
            setTab3Error(err?.message || "Final-6 failed.");
            setTab3Result(null);
        } finally {
            setTab3Loading(false);
        }
    };

    const handleTab2PlaceWord = async () => {
        if (!tab2Item || !onTab2Add) return;
        await onTab2Add(tab2Item);
        setTab2Items((prev) => prev.filter((w) => w !== tab2Item));
    };

    const handleTab2Place = async () => {
        const cartCount = cart.length;
        if (cartCount > 0) {
            if (typeof onPlaceWords === "function") {
                await onPlaceWords(cart);
                const cartKeys = new Set(cart.map((c) => `${(c.word || "").trim()}|${[...getPositionsSet(c)].sort().join(",")}`));
                setTab2Items((prev) => prev.filter((w) => !cartKeys.has(`${(w.word || "").trim()}|${[...getPositionsSet(w)].sort().join(",")}`)));
                setCart([]);
            } else if (onTab2Add) {
                const cartKeys = new Set(cart.map((c) => `${(c.word || "").trim()}|${[...getPositionsSet(c)].sort().join(",")}`));
                for (const item of cart) {
                    await onTab2Add(item);
                }
                setCart([]);
                setTab2Items((prev) =>
                    prev.filter((w) => !cartKeys.has(`${(w.word || "").trim()}|${[...getPositionsSet(w)].sort().join(",")}`))
                );
            }
        } else if (tab2Item && onTab2Add) {
            await handleTab2PlaceWord();
        }
    };

    const handleTab3PlaceWord = async () => {
        if (!tab3Item || !onTab3Add) return;
        await onTab3Add(tab3Item);
        setTab3Result((prev) =>
            prev?.wordsWithPositions
                ? { ...prev, wordsWithPositions: prev.wordsWithPositions.filter((w) => w !== tab3Item) }
                : prev
        );
    };

    const handleTab3Place = async () => {
        const cartCount = cart.length;
        if (cartCount > 0) {
            if (typeof onPlaceWords === "function") {
                await onPlaceWords(cart);
                const cartKeys = new Set(cart.map((c) => `${(c.word || "").trim()}|${[...getPositionsSet(c)].sort().join(",")}`));
                setTab3Result((prev) => {
                    if (!prev?.wordsWithPositions) return prev;
                    return { ...prev, wordsWithPositions: prev.wordsWithPositions.filter((w) => !cartKeys.has(`${(w.word || "").trim()}|${[...getPositionsSet(w)].sort().join(",")}`)) };
                });
                setCart([]);
            } else if (onTab3Add) {
                const cartKeys = new Set(cart.map((c) => `${(c.word || "").trim()}|${[...getPositionsSet(c)].sort().join(",")}`));
                for (const item of cart) {
                    await onTab3Add(item);
                }
                setCart([]);
                setTab3Result((prev) => {
                    if (!prev?.wordsWithPositions) return prev;
                    return { ...prev, wordsWithPositions: prev.wordsWithPositions.filter((w) => !cartKeys.has(`${(w.word || "").trim()}|${[...getPositionsSet(w)].sort().join(",")}`)) };
                });
            }
        } else if (tab3Item && onTab3Add) {
            await handleTab3PlaceWord();
        }
    };

    const actualContent = () => {
        if (activeTab === 1) {
            return (
                <>
                    <Alert severity="info" sx={{ mb: 2, alignItems: "center" }} icon={<AutoAwesomeIcon sx={{ fontSize: 18 }} />} size="small">
                        Find associated words
                    </Alert>
                    {tab1Error && (
                        <Typography color="error" variant="body2" sx={{ mb: 1 }}>
                            {tab1Error}
                        </Typography>
                    )}
                    {tab1Result == null && !tab1Error && (
                        <Typography variant="body2" color="text.secondary">
                            Result list will appear here after clicking Find.
                        </Typography>
                    )}
                    {tab1Total === 0 && tab1Result != null && !tab1Error && (
                        <Typography color="text.secondary" variant="body2">
                            No words with positions returned.
                        </Typography>
                    )}
                    {tab1Total > 0 && tab1Item && (
                        <Box sx={{ py: 1 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1 }}>
                                <Typography variant="h6" fontWeight={600} sx={{ flexShrink: 0 }}>
                                    {tab1Item.word ?? ""}
                                </Typography>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 0, justifyContent: "center" }}>
                                    {tab1Item.info?.score != null && (
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            score: {typeof tab1Item.info.score === "number" ? tab1Item.info.score.toLocaleString() : tab1Item.info.score}
                                        </Typography>
                                    )}
                                    {(tab1Item.coordinates?.positions ?? tab1Item.positions)?.length > 0 && (
                                        <Typography variant="caption" color="text.secondary">
                                            Positions: {(tab1Item.coordinates?.positions ?? tab1Item.positions).map((p) => `(${p.row},${p.col})`).join(", ")}, <Box component="span" sx={{ fontWeight: 700, color: "success.main" }}>{countCellsWillFill(tab1Item.coordinates?.positions ?? tab1Item.positions, filledCellKeys)} cells will fill</Box>
                                        </Typography>
                                    )}
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }} />
                                {typeof onAddBannedWord === "function" && (tab1Item.word ?? "").trim() && (
                                    <Tooltip title="Add as banned word">
                                        <IconButton
                                            type="button"
                                            size="small"
                                            onClick={() => onAddBannedWord((tab1Item.word ?? "").trim())}
                                            sx={{ p: 0.25, flexShrink: 0, color: "error.main" }}
                                            aria-label="Add as banned word"
                                        >
                                            <BlockIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                {getPositionsSet(tab1Item).size > 0 && countCellsWillFill(tab1Item.coordinates?.positions ?? tab1Item.positions, filledCellKeys) > 0 && (
                                    <Tooltip title={isItemInCart(tab1Item) ? "In cart" : "Add to cart"}>
                                        <span>
                                            <IconButton
                                                type="button"
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    addToCart(tab1Item);
                                                }}
                                                disabled={isItemInCart(tab1Item)}
                                                sx={{ p: 0.25, flexShrink: 0 }}
                                                aria-label="Add to cart"
                                            >
                                                <AddShoppingCartIcon fontSize="small" color={isItemInCart(tab1Item) ? "disabled" : "primary"} />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                )}
                            </Box>
                            {tab1Item.meaning && typeof tab1Item.meaning === "object" && (
                                <Box sx={{ mb: 1 }}>{formatMeaning(tab1Item.meaning)}</Box>
                            )}
                        </Box>
                    )}
                </>
            );
        }
        if (activeTab === 2) {
            return (
                <>
                    <Alert severity="info" sx={{ mb: 2, alignItems: "center" }} icon={<MenuBookIcon sx={{ fontSize: 18 }} />} size="small">
                        Search by puzzle tags
                    </Alert>
                    {tab2Total === 0 && !tab2Loading && (
                        <Typography variant="body2" color="text.secondary">
                            Result list will appear here after clicking Find.
                        </Typography>
                    )}
                    {tab2Total > 0 && tab2Item && (
                        <Box sx={{ py: 1 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1 }}>
                                <Typography variant="h6" fontWeight={600} sx={{ flexShrink: 0 }}>
                                    {tab2Item.word}
                                </Typography>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 0, justifyContent: "center" }}>
                                    {tab2Item.info?.score != null && (
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            score: {typeof tab2Item.info.score === "number" ? tab2Item.info.score.toLocaleString() : tab2Item.info.score}
                                        </Typography>
                                    )}
                                    {((tab2Item.coordinates?.positions ?? tab2Item.positions) || []).length > 0 && (
                                        <Typography variant="caption" color="text.secondary">
                                            Positions: {(tab2Item.coordinates?.positions ?? tab2Item.positions).map((p) => `(${p.row},${p.col})`).join(", ")}, <Box component="span" sx={{ fontWeight: 700, color: "success.main" }}>{countCellsWillFill(tab2Item.coordinates?.positions ?? tab2Item.positions, filledCellKeys)} cells will fill</Box>
                                        </Typography>
                                    )}
                                </Box>
                                {tab2Item.direction && (
                                    <Chip label={tab2Item.direction} size="small" variant="outlined" sx={{ flexShrink: 0 }} />
                                )}
                                <Box sx={{ flex: 1, minWidth: 0 }} />
                                {typeof onAddBannedWord === "function" && (tab2Item.word ?? "").trim() && (
                                    <Tooltip title="Add as banned word">
                                        <IconButton
                                            type="button"
                                            size="small"
                                            onClick={() => onAddBannedWord((tab2Item.word ?? "").trim())}
                                            sx={{ p: 0.25, flexShrink: 0, color: "error.main" }}
                                            aria-label="Add as banned word"
                                        >
                                            <BlockIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                {getPositionsSet(tab2Item).size > 0 && countCellsWillFill(tab2Item.coordinates?.positions ?? tab2Item.positions, filledCellKeys) > 0 && (
                                    <Tooltip title={isItemInCart(tab2Item) ? "In cart" : "Add to cart"}>
                                        <span>
                                            <IconButton
                                                type="button"
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    addToCart(tab2Item);
                                                }}
                                                disabled={isItemInCart(tab2Item)}
                                                sx={{ p: 0.25, flexShrink: 0 }}
                                                aria-label="Add to cart"
                                            >
                                                <AddShoppingCartIcon fontSize="small" color={isItemInCart(tab2Item) ? "disabled" : "primary"} />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                )}
                            </Box>
                            {tab2Item.meaning && typeof tab2Item.meaning === "object" && (
                                <Box sx={{ mb: 1 }}>{formatMeaning(tab2Item.meaning)}</Box>
                            )}
                        </Box>
                    )}
                </>
            );
        }
        if (activeTab === 3) {
            return (
                <>
                    <Alert severity="info" sx={{ mb: 2, alignItems: "center" }} icon={<MenuBookIcon sx={{ fontSize: 18 }} />} size="small">
                        Find 4–6 letter slots (left-to-right, top-to-bottom).
                    </Alert>
                    {tab3Error && (
                        <Typography color="error" variant="body2" sx={{ mb: 1 }}>
                            {tab3Error}
                        </Typography>
                    )}
                    {tab3Result == null && !tab3Error && (
                        <Typography variant="body2" color="text.secondary">
                            Result list will appear here after clicking Find.
                        </Typography>
                    )}
                    {tab3Total === 0 && tab3Result != null && !tab3Error && (
                        <Typography color="text.secondary" variant="body2">
                            No words with positions returned.
                        </Typography>
                    )}
                    {tab3Total > 0 && tab3Item && (
                        <Box sx={{ py: 1 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1 }}>
                                <Typography variant="h6" fontWeight={600} sx={{ flexShrink: 0 }}>
                                    {tab3Item.word ?? ""}
                                </Typography>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 0, justifyContent: "center" }}>
                                    {tab3Item.info?.score != null && (
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            score: {typeof tab3Item.info.score === "number" ? tab3Item.info.score.toLocaleString() : tab3Item.info.score}
                                        </Typography>
                                    )}
                                    {((tab3Item.coordinates?.positions ?? tab3Item.positions) || []).length > 0 && (
                                        <Typography variant="caption" color="text.secondary">
                                            Positions: {(tab3Item.coordinates?.positions ?? tab3Item.positions).map((p) => `(${p.row},${p.col})`).join(", ")}, <Box component="span" sx={{ fontWeight: 700, color: "success.main" }}>{countCellsWillFill(tab3Item.coordinates?.positions ?? tab3Item.positions, filledCellKeys)} cells will fill</Box>
                                        </Typography>
                                    )}
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }} />
                                {typeof onAddBannedWord === "function" && (tab3Item.word ?? "").trim() && (
                                    <Tooltip title="Add as banned word">
                                        <IconButton
                                            type="button"
                                            size="small"
                                            onClick={() => onAddBannedWord((tab3Item.word ?? "").trim())}
                                            sx={{ p: 0.25, flexShrink: 0, color: "error.main" }}
                                            aria-label="Add as banned word"
                                        >
                                            <BlockIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                {getPositionsSet(tab3Item).size > 0 && countCellsWillFill(tab3Item.coordinates?.positions ?? tab3Item.positions, filledCellKeys) > 0 && (
                                    <Tooltip title={isItemInCart(tab3Item) ? "In cart" : "Add to cart"}>
                                        <span>
                                            <IconButton
                                                type="button"
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    addToCart(tab3Item);
                                                }}
                                                disabled={isItemInCart(tab3Item)}
                                                sx={{ p: 0.25, flexShrink: 0 }}
                                                aria-label="Add to cart"
                                            >
                                                <AddShoppingCartIcon fontSize="small" color={isItemInCart(tab3Item) ? "disabled" : "primary"} />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                )}
                            </Box>
                            {tab3Item.meaning && typeof tab3Item.meaning === "object" && (
                                <Box sx={{ mb: 1 }}>{formatMeaning(tab3Item.meaning)}</Box>
                            )}
                        </Box>
                    )}
                </>
            );
        }
        return null;
    };

    const cartCount = cart.length;
    const clearCart = (e) => {
        if (e) {
            e.stopPropagation();
            e.preventDefault();
        }
        setCart([]);
    };

    const renderFooter = () => {
        if (activeTab === 1) {
            return (
                <Box sx={{ display: "flex", flexGrow: 1, justifyContent: "space-between", alignItems: "center", gap: { xs: 0.5, sm: 1 }, flexWrap: { xs: "nowrap", sm: "wrap" } }}>
                    <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 0.5 }}>
                        {(tab1Total > 0 || cartCount > 0) && (
                            <Button
                                type="button"
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={handleTab1Place}
                                disabled={adding}
                                sx={{ minWidth: { xs: "auto", sm: 64 }, px: { xs: 1, sm: 2 } }}
                            >
                                {adding ? "Adding…" : cartCount > 0 ? `Place (${cartCount})` : "Place"}
                            </Button>
                        )}
                        {cartCount > 0 && (
                            <Tooltip title="Clear cart">
                                <IconButton type="button" size="small" onClick={clearCart} aria-label="Clear cart" sx={{ p: 0.5 }}>
                                    <RemoveShoppingCartIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 1 }, flexShrink: 0, marginLeft: "auto" }}>
                        {tab1Total > 0 && (
                            <>
                                <IconButton size="small" onClick={() => setTab1Index((i) => Math.max(0, i - 1))} disabled={tab1Index <= 0} aria-label="Previous">
                                    <ChevronLeftIcon />
                                </IconButton>
                                <Typography variant="body2" color="text.secondary" noWrap>
                                    {tab1Index + 1} / {tab1Total}
                                </Typography>
                                <IconButton size="small" onClick={() => setTab1Index((i) => Math.min(tab1Total - 1, i + 1))} disabled={tab1Index >= tab1Total - 1} aria-label="Next">
                                    <ChevronRightIcon />
                                </IconButton>
                            </>
                        )}
                        <Button
                            type="button"
                            variant="outlined"
                            size="small"
                            onClick={handleTab1Refresh}
                            disabled={tab1Loading}
                            sx={{ minWidth: { xs: "auto", sm: 64 }, px: { xs: 1, sm: 2 } }}
                        >
                            {tab1Loading ? "Finding…" : "Find"}
                        </Button>
                    </Box>
                </Box>
            );
        }
        if (activeTab === 2) {
            return (
                <Box sx={{ display: "flex", flexGrow: 1, justifyContent: "space-between", alignItems: "center", gap: { xs: 0.5, sm: 1 }, flexWrap: { xs: "nowrap", sm: "wrap" } }}>
                    <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 0.5 }}>
                        {(tab2Total > 0 || cartCount > 0) && (
                            <Button
                                type="button"
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={handleTab2Place}
                                disabled={adding}
                                sx={{ minWidth: { xs: "auto", sm: 64 }, px: { xs: 1, sm: 2 } }}
                            >
                                {adding ? "Adding…" : cartCount > 0 ? `Place (${cartCount})` : "Place"}
                            </Button>
                        )}
                        {cartCount > 0 && (
                            <Tooltip title="Clear cart">
                                <IconButton type="button" size="small" onClick={clearCart} aria-label="Clear cart" sx={{ p: 0.5 }}>
                                    <RemoveShoppingCartIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 1 }, flexShrink: 0, marginLeft: "auto" }}>
                        {tab2Total > 0 && (
                            <>
                                <IconButton size="small" onClick={() => setTab2Index((i) => Math.max(0, i - 1))} disabled={tab2Index <= 0} aria-label="Previous">
                                    <ChevronLeftIcon />
                                </IconButton>
                                <Typography variant="body2" color="text.secondary" noWrap>
                                    {tab2Index + 1} / {tab2Total}
                                </Typography>
                                <IconButton size="small" onClick={() => setTab2Index((i) => Math.min(tab2Total - 1, i + 1))} disabled={tab2Index >= tab2Total - 1} aria-label="Next">
                                    <ChevronRightIcon />
                                </IconButton>
                            </>
                        )}
                        <Button
                            type="button"
                            variant="outlined"
                            size="small"
                            onClick={handleTab2Refresh}
                            disabled={tab2Loading || adding}
                            sx={{ minWidth: { xs: "auto", sm: 64 }, px: { xs: 1, sm: 2 } }}
                        >
                            {tab2Loading ? "Finding…" : "Find"}
                        </Button>
                    </Box>
                </Box>
            );
        }
        if (activeTab === 3) {
            return (
                <Box sx={{ display: "flex", flexGrow: 1, justifyContent: "space-between", alignItems: "center", gap: { xs: 0.5, sm: 1 }, flexWrap: { xs: "nowrap", sm: "wrap" } }}>
                    <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 0.5 }}>
                        {(tab3Total > 0 || cartCount > 0) && (
                            <Button
                                type="button"
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={handleTab3Place}
                                disabled={adding}
                                sx={{ minWidth: { xs: "auto", sm: 64 }, px: { xs: 1, sm: 2 } }}
                            >
                                {adding ? "Adding…" : cartCount > 0 ? `Place (${cartCount})` : "Place"}
                            </Button>
                        )}
                        {cartCount > 0 && (
                            <Tooltip title="Clear cart">
                                <IconButton type="button" size="small" onClick={clearCart} aria-label="Clear cart" sx={{ p: 0.5 }}>
                                    <RemoveShoppingCartIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 1 }, flexShrink: 0, marginLeft: "auto" }}>
                        {tab3Total > 0 && (
                            <>
                                <IconButton size="small" onClick={() => setTab3Index((i) => Math.max(0, i - 1))} disabled={tab3Index <= 0} aria-label="Previous">
                                    <ChevronLeftIcon />
                                </IconButton>
                                <Typography variant="body2" color="text.secondary" noWrap>
                                    {tab3Index + 1} / {tab3Total}
                                </Typography>
                                <IconButton size="small" onClick={() => setTab3Index((i) => Math.min(tab3Total - 1, i + 1))} disabled={tab3Index >= tab3Total - 1} aria-label="Next">
                                    <ChevronRightIcon />
                                </IconButton>
                            </>
                        )}
                        <Button
                            type="button"
                            variant="outlined"
                            size="small"
                            onClick={handleTab3Refresh}
                            disabled={tab3Loading}
                            sx={{ minWidth: { xs: "auto", sm: 64 }, px: { xs: 1, sm: 2 } }}
                        >
                            {tab3Loading ? "Finding…" : "Find"}
                        </Button>
                    </Box>
                </Box>
            );
        }
        return null;
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                fullScreen={fullScreen}
                maxWidth="sm"
                fullWidth
                aria-labelledby="cross-fill-dialog-title"
                sx={{
                    "& .MuiDialog-container": {
                        paddingTop: 0,
                        paddingBottom: 0,
                        paddingLeft: { xs: 0, sm: "8px" },
                        paddingRight: { xs: 0, sm: "8px" },
                        alignItems: { xs: "stretch", sm: "center" }
                    }
                }}
                PaperProps={{
                    sx: {
                        margin: 0,
                        maxHeight: { xs: "100vh", sm: "100vh" }
                    }
                }}
            >
                <DialogTitle id="cross-fill-dialog-title" sx={fillDialogTitleSx}>
                    <IconButton
                        aria-label="Close"
                        onClick={onClose}
                        sx={fillDialogCloseIconButtonSx}
                    >
                        <CloseIcon />
                    </IconButton>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        {puzzleType && (
                            <Chip
                                label={typeof puzzleType === "string" ? puzzleType.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : puzzleType}
                                size="small"
                                variant="outlined"
                                sx={{ height: 24, fontSize: "0.75rem" }}
                            />
                        )}
                        {puzzleTitle && (
                            <Typography
                                variant="h6"
                                color="primary"
                                sx={{ fontWeight: "bold", display: "block" }}
                            >
                                {puzzleTitle}
                            </Typography>
                        )}
                    </Box>
                    {Array.isArray(tags) && tags.length > 0 && (
                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: { xs: 0.5, sm: 1 } }}>
                            {tags
                                .filter((tag) => {
                                    const label = typeof tag === "string" ? tag : tag?.name || tag?.label || String(tag);
                                    return String(label).toLowerCase() !== "bedrock";
                                })
                                .map((tag, i) => (
                                <Chip
                                    key={i}
                                    label={typeof tag === "string" ? tag : tag?.name || tag?.label || String(tag)}
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: 24, fontSize: "0.75rem" }}
                                />
                                ))}
                        </Box>
                    )}
                </DialogTitle>
                <DialogContent dividers sx={{ minHeight: "50vh", px: { xs: 0.5, sm: 2 }, py: { xs: 1, sm: 2 } }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 1 }, flexWrap: { xs: "nowrap", sm: "wrap" }, mb: { xs: 1, sm: 2 } }}>
                        {emptyCellCount != null && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", flexShrink: 0 }}>
                                <Box
                                    sx={{
                                        px: 1.5,
                                        py: 0.5,
                                        border: "1px solid",
                                        borderColor: emptyCellCount === 0 ? "#2e7d32" : "#d32f2f",
                                        borderRadius: 1,
                                        bgcolor: emptyCellCount === 0 ? "rgba(46, 125, 50, 0.12)" : "rgba(211, 47, 47, 0.08)"
                                    }}
                                >
                                    <Typography variant="body2" sx={{ color: emptyCellCount === 0 ? "#2e7d32" : "#d32f2f", fontWeight: 600, fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                                        {emptyCellCount === 0 ? "Full Filled" : `Empty: ${Number(emptyCellCount).toLocaleString()}`}
                                    </Typography>
                                </Box>
                                {!thresholdMet && thresholdPercentage != null && cellsNeeded != null && (
                                    <Typography variant="body2" sx={{ color: "#d32f2f", fontWeight: 500, fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                                        {thresholdPercentage}% TH ({cellsNeeded} to fill)
                                    </Typography>
                                )}
                                {thresholdMet && thresholdPercentage != null && (
                                    <Typography variant="body2" sx={{ color: "#2e7d32", fontWeight: 600, fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                                        Threshold reached
                                    </Typography>
                                )}
                            </Box>
                        )}
                        <Box sx={{ flex: 1, minWidth: 0, flexShrink: 1 }} />
                        <ToggleButtonGroup
                            value={activeTab}
                            exclusive
                            onChange={handleTabChange}
                            size="small"
                            sx={{
                                flexShrink: 0,
                                height: 24,
                                "& .MuiToggleButton-root": { height: 24, minWidth: 32, py: 0, fontSize: "0.75rem" }
                            }}
                        >
                            <ToggleButton value={1}>1</ToggleButton>
                            <ToggleButton value={2}>2</ToggleButton>
                            <ToggleButton value={3}>3</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                    <Box sx={{ minHeight: "40vh" }}>{actualContent()}</Box>
                </DialogContent>
                <DialogActions sx={{ justifyContent: "flex-start", flexWrap: "wrap", gap: 1, pl: { xs: 0.5, sm: 2 }, pr: { xs: 0.5, sm: 1 }, py: { xs: 1, sm: 2 } }}>
                    {thresholdMet && typeof onReadyToReview === "function" && (
                        <Button
                            variant="outlined"
                            color="success"
                            size="small"
                            startIcon={readyToReviewLoading ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
                            onClick={onReadyToReview}
                            disabled={readyToReviewDisabled || readyToReviewLoading}
                            sx={{ minWidth: 120, whiteSpace: "nowrap" }}
                        >
                            {readyToReviewLoading ? "Sending…" : "Ready to Review"}
                        </Button>
                    )}
                    {renderFooter()}
                </DialogActions>
            </Dialog>
        </>
    );
}
